import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Vote } from "../models/vote";
import { api } from "../api";
import { VotingResults } from "../../../server/results";
import { RLink } from "../DesignSystem/RLink";
import {
  EyeSlashIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { ModerationActions } from "../../../src/moderation";

type ModerationEvent = {
  id: string;
  createdAt: string;
  actorUsername: string;
  actionType: string;
  reason: string | null;
  voteCount?: number;
};

const SingleVoteCard = ({
  vote,
  danger,
  extreme,
  wayOff,
  onIgnore,
  timeDiffSeconds,
  scoreDiff,
}: {
  vote: Vote;
  danger: boolean;
  extreme: boolean;
  wayOff: boolean;
  onIgnore: () => void;
  timeDiffSeconds?: number;
  scoreDiff?: number;
}): JSX.Element => {
  const reasons: string[] = [];
  if (extreme && timeDiffSeconds !== undefined) {
    reasons.push(`Vote made ${timeDiffSeconds} seconds after previous vote`);
  } else if (danger && timeDiffSeconds !== undefined) {
    const minutes = Math.floor(timeDiffSeconds / 60);
    const seconds = Math.round(timeDiffSeconds % 60);
    reasons.push(
      `Vote made ${minutes}m ${seconds}s after previous vote`
    );
  }
  if (wayOff && scoreDiff !== undefined) {
    reasons.push(
      `Overall score is ${scoreDiff.toFixed(1)} points away from average`
    );
  }
  const scores =
    vote.overall === null || vote.overall === undefined
      ? [
          vote.custom1,
          vote.custom2,
          vote.custom3,
          vote.custom4,
          vote.custom5,
          vote.custom6,
          vote.custom7,
          vote.custom8,
        ].filter((s) => s !== null && s !== undefined)
      : [
          vote.overall,
          vote.arrangement,
          vote.atmosphere,
          vote.composition,
          vote.production,
          vote.sounddesign,
          vote.theme,
          vote.uniqueness,
        ].filter((s) => s !== null && s !== undefined);

  const timestamp = new Date(vote.created).toLocaleString();

  let rowClass =
    "flex items-center gap-4 rounded-lg border px-4 py-3 transition ";
  if (vote.ignored) {
    rowClass +=
      "border-slate-200 bg-slate-50 opacity-50 dark:border-slate-700 dark:bg-slate-800/50";
  } else if (extreme) {
    rowClass +=
      "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20";
  } else if (danger) {
    rowClass +=
      "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20";
  } else if (wayOff) {
    rowClass +=
      "border-lime-300 bg-lime-50 dark:border-lime-800 dark:bg-lime-900/20";
  } else {
    rowClass +=
      "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800";
  }

  return (
    <div className="group relative">
      <div className={rowClass}>
        <div className="min-w-[80px] text-sm text-slate-500 dark:text-slate-400">
          User {vote.user_id}
        </div>
        <div className="min-w-[80px]">
          <RLink
            to={`/entry/${vote.entry_id}`}
            className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Entry {vote.entry_id}
          </RLink>
        </div>
        <div className="flex flex-1 gap-1.5">
          {scores.map((score, i) => (
            <span
              key={i}
              className={`inline-flex min-w-[28px] justify-center rounded px-1.5 py-0.5 text-xs font-medium ${
                wayOff && i === 0
                  ? "bg-lime-200 text-lime-800 dark:bg-lime-800 dark:text-lime-200"
                  : "bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200"
              }`}
            >
              {score}
            </span>
          ))}
        </div>
        <div className="min-w-[140px] text-xs text-slate-400 dark:text-slate-500">
          {timestamp}
        </div>
        <button
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          onClick={onIgnore}
        >
          {vote.ignored ? (
            <>
            <EyeIcon className="h-3.5 w-3.5" />
            Unignore
          </>
        ) : (
          <>
            <EyeSlashIcon className="h-3.5 w-3.5" />
            Ignore
          </>
        )}
        </button>
      </div>
      {reasons.length > 0 && (
        <div className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block dark:bg-slate-700">
          {reasons.map((reason, i) => (
            <div key={i}>{reason}</div>
          ))}
        </div>
      )}
    </div>
  );
};

type VoteAuditParams = { id: string };

export const CompoVoteAuditPage = (): JSX.Element => {
  const { id } = useParams<VoteAuditParams>();
  const [votes, setVotes] = useState<Vote[]>([]);
  const [votingResults, setVotingResults] = useState<VotingResults>();
  const [activityLogUserId, setActivityLogUserId] = useState<string | null>(
    null
  );
  const [activityLog, setActivityLog] = useState<ModerationEvent[]>([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);

  const fetchVotes = async () => {
    const voteResponse = await api.get<"/api/compo/:id/admin/voteaudit">(
      `/api/compo/${id}/admin/voteaudit`
    );
    if (!api.isToast(voteResponse)) {
      setVotes(voteResponse.votes.map((vote) => new Vote(vote)));
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      const results = await api.get<"/api/compo/:id/results">(
        `/api/compo/${id}/results`
      );
      if (api.isToast(results) || cancelled) {
        return;
      }

      const voteResponse = await api.get<"/api/compo/:id/admin/voteaudit">(
        `/api/compo/${id}/admin/voteaudit`
      );

      if (api.isToast(voteResponse) || cancelled) {
        return;
      }

      setVotingResults(results);
      setVotes(voteResponse.votes.map((vote) => new Vote(vote)));
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleIgnoreVote = async (voteId: number) => {
    await api.post<"/api/compo/vote/:id/hide">(`/api/compo/vote/${voteId}/hide`);
    await fetchVotes();
  };

  const handleIgnoreAllUserVotes = async (userId: string) => {
    await api.post<"/api/compo/:compo_id/admin/user/:user_id/ignore-all-votes">(
      `/api/compo/${id}/admin/user/${userId}/ignore-all-votes`
    );
    await fetchVotes();
  };

  const openActivityLog = async (userId: string) => {
    setActivityLogUserId(userId);
    setActivityLogLoading(true);
    const result =
      await api.get<"/api/compo/:compo_id/admin/user/:user_id/vote-moderation-log">(
        `/api/compo/${id}/admin/user/${userId}/vote-moderation-log`
      );
    if (!api.isToast(result)) {
      setActivityLog(result.events);
    }
    setActivityLogLoading(false);
  };

  const closeActivityLog = () => {
    setActivityLogUserId(null);
    setActivityLog([]);
  };

  const votesPerUser = useMemo(() => {
    const grouped: Record<string, Vote[]> = {};
    votes.forEach((vote) => {
      if (!grouped[vote.user_id]) {
        grouped[vote.user_id] = [];
      }
      grouped[vote.user_id].push(vote);
    });

    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
      );
    });

    return grouped;
  }, [votes]);

  const userStats = useMemo(() => {
    if (!votingResults) return [];

    return Object.keys(votesPerUser)
      .map((userId) => {
        let tempLast = 0;
        let extremeCount = 0;
        let dangerCount = 0;
        let wayOffCount = 0;
        let sketchySum = 0;
        const userVotes = votesPerUser[userId];
        const totalVotes = userVotes.length;
        const allIgnored = userVotes.every((v) => v.ignored);

        userVotes.forEach((vote) => {
          const thisTime = new Date(vote.created).getTime();
          const diffInMinutes = (thisTime - tempLast) / (1000 * 60);
          const association = votingResults.categories[0]?.find(
            (row) => row.entry.id === vote.entry_id
          );

          const isExtreme = tempLast !== 0 && Math.abs(diffInMinutes) < 0.5;
          const isDanger =
            tempLast !== 0 && Math.abs(diffInMinutes) < 2 && !isExtreme;
          const isWayOff =
            association && Math.abs(vote.custom1! - association.score) >= 2.5;

          if (isExtreme) extremeCount++;
          if (isDanger) dangerCount++;
          if (isWayOff) wayOffCount++;

          // Calculate sketchiness for this vote (capped at 1.0)
          let voteSketchiness = 0;
          if (isExtreme) voteSketchiness = 1.0;
          else if (isDanger) voteSketchiness = 0.5;
          if (isWayOff) voteSketchiness = Math.min(1.0, voteSketchiness + 0.25);
          sketchySum += voteSketchiness;

          tempLast = thisTime;
        });

        const sketchyPercent =
          totalVotes > 0 ? (sketchySum / totalVotes) * 100 : 0;

        return {
          userId,
          extremeCount,
          dangerCount,
          wayOffCount,
          sketchySum,
          sketchyPercent,
          totalVotes,
          allIgnored,
        };
      })
      .filter((s) => s.sketchySum > 0)
      .sort((a, b) => b.sketchySum - a.sketchySum);
  }, [votesPerUser, votingResults]);

  if (votes.length === 0 || !votingResults) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 pb-8 pt-16 dark:bg-slate-900">
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-2 text-2xl font-bold text-slate-800 dark:text-white">
          Vote Auditor
        </h1>
        <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
          <span className="mr-4 inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-red-200 dark:bg-red-800"></span>
            less than 30s apart
          </span>
          <span className="mr-4 inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-amber-200 dark:bg-amber-800"></span>
            less than 2m apart
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-lime-200 dark:bg-lime-800"></span>
            Score 2.5+ off average
          </span>
        </p>

        {userStats.length > 0 && (
          <div className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-800">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-700/50">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Most Questionable Voters
              </span>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {userStats.map((stat) => (
                <div
                  key={stat.userId}
                  className="flex items-center justify-between px-4 py-2"
                >
                  <div className="flex items-center gap-4">
                    <span className="min-w-[80px] text-sm font-medium text-slate-800 dark:text-white">
                      User {stat.userId}
                    </span>
                    <div className="flex gap-3 text-xs">
                      {stat.extremeCount > 0 && (
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-red-500"></span>
                          {stat.extremeCount} less than 30s
                        </span>
                      )}
                      {stat.dangerCount > 0 && (
                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-amber-500"></span>
                          {stat.dangerCount} less than 2m
                        </span>
                      )}
                      {stat.wayOffCount > 0 && (
                        <span className="flex items-center gap-1 text-lime-600 dark:text-lime-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-lime-500"></span>
                          {stat.wayOffCount} off avg
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {stat.totalVotes} votes
                    </span>
                    <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-600 dark:text-slate-200">
                      {stat.sketchyPercent.toFixed(0)}% sketchy
                    </span>
                    <button
                      onClick={() => handleIgnoreAllUserVotes(stat.userId)}
                      className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition ${
                        stat.allIgnored
                          ? "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                          : "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                      }`}
                    >
                      {stat.allIgnored ? (
                        <>
                          <EyeIcon className="h-3.5 w-3.5" />
                          Unignore all votes
                        </>
                      ) : (
                        <>
                          <EyeSlashIcon className="h-3.5 w-3.5" />
                          Ignore all votes
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => openActivityLog(stat.userId)}
                      className="flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                    >
                      <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                      Log
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {Object.keys(votesPerUser).map((userId) => {
            // Pre-calculate stats for this user
            let tempLast = 0;
            let extremeCount = 0;
            let dangerCount = 0;
            let wayOffCount = 0;

            votesPerUser[userId].forEach((vote) => {
              const thisTime = new Date(vote.created).getTime();
              const diffInMinutes = (thisTime - tempLast) / (1000 * 60);
              const association = votingResults.categories[0]?.find(
                (row) => row.entry.id === vote.entry_id
              );

              if (tempLast !== 0 && Math.abs(diffInMinutes) < 0.5)
                extremeCount++;
              else if (tempLast !== 0 && Math.abs(diffInMinutes) < 2)
                dangerCount++;
              if (
                association &&
                Math.abs(vote.custom1! - association.score) >= 2.5
              )
                wayOffCount++;

              tempLast = thisTime;
            });

            let last = 0;
            return (
              <div
                key={userId}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-800"
              >
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        User {userId}
                      </span>
                      <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                        ({votesPerUser[userId].length} votes)
                      </span>
                    </div>
                    <div className="flex gap-3 text-xs">
                      {extremeCount > 0 && (
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-red-500"></span>
                          {extremeCount} less than 30s
                        </span>
                      )}
                      {dangerCount > 0 && (
                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-amber-500"></span>
                          {dangerCount} less than 2m
                        </span>
                      )}
                      {wayOffCount > 0 && (
                        <span className="flex items-center gap-1 text-lime-600 dark:text-lime-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-lime-500"></span>
                          {wayOffCount} off avg
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2 p-3">
                  {votesPerUser[userId].map((vote, idx) => {
                    const thisTime = new Date(vote.created).getTime();
                    const diffInMinutes = (thisTime - last) / (1000 * 60);
                    const association = votingResults.categories[0]?.find(
                      (row) => row.entry.id === vote.entry_id
                    );

                    const diffInSeconds = (thisTime - last) / 1000;
                    const isExtreme =
                      last !== 0 && Math.abs(diffInMinutes) < 0.5;
                    const isDanger =
                      last !== 0 && Math.abs(diffInMinutes) < 2 && !isExtreme;
                    const prevLast = last;
                    last = thisTime;

                    if (!association) {
                      return (
                        <div
                          key={vote.id || idx}
                          className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                        >
                          Hidden entry
                        </div>
                      );
                    }

                    const scoreDiff = Math.abs(vote.custom1! - association.score);
                    const isWayOff = scoreDiff >= 2.5;

                    return (
                      <SingleVoteCard
                        key={vote.id || idx}
                        vote={vote}
                        danger={isDanger}
                        wayOff={isWayOff}
                        extreme={isExtreme}
                        onIgnore={() => handleIgnoreVote(vote.id)}
                        timeDiffSeconds={prevLast !== 0 ? Math.round(diffInSeconds) : undefined}
                        scoreDiff={isWayOff ? scoreDiff : undefined}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity Log Modal */}
      {activityLogUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeActivityLog}
          />
          <div className="relative mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Vote Moderation Log - User {activityLogUserId}
            </h2>
            <div className="mt-4">
              {activityLogLoading ? (
                <div className="text-slate-600 dark:text-slate-300">
                  Loading...
                </div>
              ) : activityLog.length === 0 ? (
                <div className="text-slate-600 dark:text-slate-300">
                  No moderation activity recorded.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 pr-3 text-left font-medium text-slate-600 dark:text-slate-400">
                        Action
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-400">
                        By
                      </th>
                      <th className="py-2 pl-3 text-left font-medium text-slate-600 dark:text-slate-400">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLog.map((event) => (
                      <tr
                        key={event.id}
                        className="border-b border-slate-100 dark:border-slate-700/50"
                      >
                        <td className="py-2 pr-3 text-slate-800 dark:text-slate-200">
                          {ModerationActions[
                            event.actionType as keyof typeof ModerationActions
                          ] || event.actionType}
                          {event.voteCount && (
                            <span className="ml-1 text-slate-500 dark:text-slate-400">
                              ({event.voteCount} votes)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-800 dark:text-slate-200">
                          {event.actorUsername}
                        </td>
                        <td className="py-2 pl-3 text-slate-500 dark:text-slate-400">
                          {new Date(event.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={closeActivityLog}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
